/**
 * Copyright (c) 2019, Trend Micro, Inc.
 * All rights reserved.
 */

export const c1JsCollection = [];
export const c1CssCollection = [];

// append script tag to DOM dynamically If no valid parent element is provided, it will be appended to <head>...</head>
// i.e. <script id="scriptID" src="url"></script>
export const appendScriptTagWithPromise = (id, url, parentElement) =>
  new Promise((resolve, reject) => {
    return appendScriptTag(id, url, parentElement, resolve, () =>
      reject(new Error(`Failed to load JS from: '${url}'`))
    );
  });

// similar to above, but allows passing in raw script, it loads syncronously, and
// we just call the onload immediately
export const appendScriptTextWithPromise = (id, src, parentElement) =>
  new Promise((resolve, reject) => {
    // we resolve directly after, as the script is processed immediately, and onload doesn't work,
    // so we call it directly ourselves
    appendScriptTag(
      id,
      null,
      parentElement,
      null,
      () => reject(new Error(`Failed to load JS from: '${src}'`)),
      src
    );
    resolve();
  });

// append script tag to DOM dynamically. If no valid parent element is provided, it will be appended to <head>...</head>
// if you want to append script in text as opposed to a remote url, specify it in text, and set url to null.
// NOTE: If specifying text, onload/onerror likely will not work.
const appendScriptTag = (id, url, parentElement, onload = null, onerror = null, text = null) => {
  if (!parentElement) parentElement = window.document;
  const scriptTag = document.createElement('script');
  scriptTag.id = id;
  if (text) {
    scriptTag.text = text;
  } else {
    scriptTag.src = url;
  }
  // HTML5 specification urges authors to omit the attribute rather than provide a redundant MIME type - hence omiting type = 'text/javascript' on purpose
  // Not setting it to async because we would like to ensure the injected scripts loads first using promise
  if (onload) scriptTag.onload = onload;
  if (onerror) scriptTag.onerror = onerror;
  else scriptTag.onerror = () => new Error(`Failed to load JS from: '${url}'`);

  const firstScript = parentElement.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode)
    firstScript.parentNode.insertBefore(scriptTag, firstScript);
  else if (parentElement) parentElement.appendChild(scriptTag);
  else (document.head || document.getElementsByTagName('head')[0]).appendChild(scriptTag);
};

export const appendMicroFrontendJsTags = async (appJSTagsToAppend) => {
  if (appJSTagsToAppend.length > 0) {
    // Note: microfrontend JS tags needs to be injected in the particular order below

    // 1. Load the chunks first
    const jsChunkEntries = appJSTagsToAppend.filter(
      (jsTagEntry) => jsTagEntry.bundleType === 'chunk'
    );
    await Promise.all(
      jsChunkEntries.map(async (jsChunkEntry) => {
        const script = document.getElementById(jsChunkEntry.jsId);
        if (!script) {
          await appendScriptTagWithPromise(
            jsChunkEntry.jsId,
            jsChunkEntry.url,
            document.head || document.getElementsByTagName('head')[0]
          );
        } else {
          console.debug('Skip appending already existed jsChunk tag ID:', jsChunkEntry.jsId);
        }
      })
    );

    // 2. Load the app bundle

    // 2.1 Load non-main bundles first
    const jsNonMainBundleEntries = appJSTagsToAppend.filter(
      (jsTagEntry) => jsTagEntry.bundleType === 'bundle' && jsTagEntry.isMainBundle === false
    );
    await Promise.all(
      jsNonMainBundleEntries.map(async (jsNonMainBundle) => {
        const script = document.getElementById(jsNonMainBundle.jsId);
        if (!script) {
          await appendScriptTagWithPromise(
            jsNonMainBundle.jsId,
            jsNonMainBundle.url,
            document.head || document.getElementsByTagName('head')[0]
          );
        } else {
          console.debug(
            'Skip appending already existed jsNonMainBundle tag ID:',
            jsNonMainBundle.jsId
          );
        }
      })
    );
    // 2.2 Then load main bundle
    const jsMainBundleEntries = appJSTagsToAppend.filter(
      (jsTagEntry) => jsTagEntry.bundleType === 'bundle' && jsTagEntry.isMainBundle === true
    );
    await Promise.all(
      jsMainBundleEntries.map(async (jsMainBundle) => {
        const script = document.getElementById(jsMainBundle.jsId);
        if (!script) {
          await appendScriptTagWithPromise(
            jsMainBundle.jsId,
            jsMainBundle.url,
            document.head || document.getElementsByTagName('head')[0]
          );
        } else {
          console.debug('Skip appending already existed jsMainBundle tag ID:', jsMainBundle.jsId);
        }
      })
    );
  } else {
    console.debug('No JS tag will be appended');
  }
};

export const removeScript = (id) => {
  const script = document.getElementById(id);
  if (script) script.remove();
};

export const toggleCssTag = (id, applyCss) => {
  const cssTag = document.getElementById(id);
  if (cssTag && cssTag.tagName === 'LINK') {
    // Note: not using rel='preload' approach since browser support for preload is still not great and prevent unintentional render blocking (when switching back to landing web app)
    if (applyCss) {
      // set link's media back to `all` so that the stylesheet applies once it loads
      cssTag.media = 'all';
    } else {
      // temporarily set media to something inapplicable to ensure browser will fetch CSS asynchronously without blocking render to support quick CSS switching upon app mounting/un-mounting
      // this will completely eliminate the undesirable "un-styled UI content" repaint / reflow effect that is briefly visible during transition (i.e. if the CSS <link/> tags were removed and re-added programmatically)
      cssTag.media = 'print';
    }
  }
};

// append link tag to DOM dynamically once promise is resolved
// i.e. <head><link rel="stylesheet" type="text/css"  href="http://.../styles.css"></head>
export const appendCssTagWithPromise = (id, url) => {
  new Promise((resolve, reject) => {
    const cssTag = document.createElement('link');
    cssTag.id = id;
    cssTag.href = url;
    cssTag.rel = 'stylesheet';
    cssTag.type = 'text/css';
    cssTag.onload = resolve;
    cssTag.onerror = () => reject(new Error(`Failed to download CSS from URL: '${url}'`));

    document.getElementsByTagName('head')[0].appendChild(cssTag);
  });
};

export const getScriptModule = (moduleName) => {
  if (global[moduleName]) {
    console.log(`Successfully get module "${moduleName}"`);
    console.dir(global[moduleName]);
    return global[moduleName];
  }
  throw new Error(`Failed to get module "${moduleName}"`);
};

// const loadRemoteScript = async (url, moduleName) => {
//   await appendScriptTagWithPromise("id", url);
//   return getScriptModule(moduleName);
// };

export const matchPath = (pathnames) => (location) =>
  pathnames.some((pathname) => location.pathname === pathname);

export const remoteDynamicImportMainUMDModule = async (url, moduleName, appType) => {
  await import(/* webpackIgnore: true */ url);
  if (appType === 'angular') return getScriptModule(moduleName).default;
  else return getScriptModule(moduleName);
};

export const remoteDynamicImportModule = async (
  url,
  appName,
  appType,
  bundleType,
  isMainBundle
) => {
  // Special treatment for Conformity ember micro-frontend.
  // TODO: Need to investigate if there is a better way of importing Ember app's JS, also removing these JS tags upon unmount
  if (appType === 'ember') {
    console.debug(
      `remoteDynamicImportModule >> bundleType, isMainBundle:`,
      bundleType,
      isMainBundle
    );
    const jsHash = url.substring(url.length - 8, url.length - 3);
    const jsId = `js-${appName}-${jsHash}`;
    c1JsCollection.push({ appName, jsId, url, bundleType, isMainBundle });
    // console.debug(`remoteDynamicImportModule >> Appending c1JsCollection Script tag:`, jsId);
    console.debug(
      `remoteDynamicImportModule >> Current c1JsCollection: ${JSON.stringify(c1JsCollection)}`
    );
    return appendScriptTagWithPromise(
      jsId,
      url,
      document.head || document.getElementsByTagName('head')[0]
    );
  }

  // For loading ES Module or non-main bundles, we don't need to load the module from global
  return import(/* webpackIgnore: true */ url);
};

const joinPath = (...args) => {
  return args
    .map((part, i) => {
      if (i === 0) {
        return part.trim().replace(/[\/]*$/g, '');
      } else {
        return part.trim().replace(/(^[\/]*|[\/]*$)/g, '');
      }
    })
    .filter((x) => x.length)
    .join('/');
};

const isValidAppManifest = (appManifestObj) => {
  let isValid = false;
  console.table(appManifestObj);
  console.table(appManifestObj.content);

  // check for essential properties
  if (
    appManifestObj.name &&
    appManifestObj.name.trim() !== '' &&
    ((appManifestObj.dsAppName && appManifestObj.dsAppName.trim() !== '') ||
      (appManifestObj.appName && appManifestObj.appName.trim() !== '')) &&
    appManifestObj.version &&
    appManifestObj.version.trim() !== '' &&
    appManifestObj.jsModuleFormat &&
    appManifestObj.jsModuleFormat.trim() !== '' &&
    appManifestObj.appType &&
    appManifestObj.appType.trim() !== '' &&
    appManifestObj.content &&
    appManifestObj.content.length > 0
  ) {
    isValid = true;
  }
  return isValid;
};

export async function loadAppManifest(contentURLPrefix, manifestFileName) {
  // fetch the .JSON manifest file dynamically
  try {
    const response = await fetch(
      joinPath(contentURLPrefix, manifestFileName || 'app-manifest.json'),
      {
        headers: {
          Accept: 'application/json',
        },
      }
    );
    if (!response.ok) {
      throw new Error('Network response for app manifest request did not succeed');
    }
    const contentType = response.headers.get('content-type');
    if (
      !contentType ||
      contentType.includes('text/html') ||
      !contentType.includes('application/json')
    ) {
      throw new TypeError(
        `Fallback HTML was returned. The manifest from ${contentURLPrefix} is not available`
      );
    }
    const appManifest = await response.json();
    if (isValidAppManifest(appManifest)) {
      return appManifest;
    } else {
      throw new Error(
        `Invalid app manifest: ${joinPath(
          contentURLPrefix,
          manifestFileName || 'app-manifest.json'
        )} due to invalid manifest content`
      );
    }
  } catch (error) {
    console.warn(
      `Failed to load the App Manifest. The service may not be available. Error Detail: `,
      error
    );
    throw error;
  }
}

export async function loadStore(
  contentURLPrefix,
  appManifest,
  globalEventDistributor,
  initialReduxStoreState
) {
  const appName = appManifest.appName ? appManifest.appName : appManifest.dsAppName;
  if (appManifest.hasStore === true) {
    let storeBundle = appManifest.content.find(
      (contentEntry) =>
        contentEntry.isStore === true &&
        contentEntry.bundleType === 'bundle' &&
        contentEntry.type === 'js'
    );
    let storeChunk = appManifest.content.find(
      (contentEntry) =>
        contentEntry.isStore === true &&
        contentEntry.bundleType === 'chunk' &&
        contentEntry.type === 'js'
    );
    let customProps;
    if (appManifest.jsModuleFormat === 'esm') {
      customProps = await loadESModuleStoreAsCustomProps(
        appName,
        joinPath(contentURLPrefix, storeBundle.path),
        storeChunk ? joinPath(contentURLPrefix, storeChunk.path) : null,
        globalEventDistributor,
        initialReduxStoreState
      );
    } else if (appManifest.jsModuleFormat === 'umd') {
      customProps = await loadUMDModuleStoreAsCustomProps(
        appName,
        joinPath(contentURLPrefix, storeBundle.path),
        storeChunk ? joinPath(contentURLPrefix, storeChunk.path) : null,
        globalEventDistributor,
        initialReduxStoreState
      );
    }

    return customProps;
  } else return null;
}

export async function getLoadAppFunctionOrPromise(contentURLPrefix, appManifest) {
  try {
    const appName = appManifest.appName ? appManifest.appName : appManifest.dsAppName;
    // console.table(appManifest);
    // console.table(appManifest.content);

    // 0. Load the CSS media
    // First time app loading only - Append the needed CSS <link/> tag once before JS of the microfrontend is loaded (on-demand)
    // Subsequent CSS apply/un-apply upon app mount/unmount is managed by responding to single-spa's custom event
    const mediaCSS = appManifest.content.filter(
      (contentEntry) => contentEntry.bundleType === 'media' && contentEntry.type === 'css'
    );
    await Promise.all(
      mediaCSS.map((contentEntry, index) => {
        // Keep the reference of css that can be used later upon app mount/unmount
        const cssId = `css-${appName}-${index}`;
        const cssPath = joinPath(contentURLPrefix, contentEntry.path);
        c1CssCollection.push({ appName, cssId, cssPath });
        appendCssTagWithPromise(cssId, cssPath);
      })
    );

    // 1. Load the chunks first
    const chunks = appManifest.content.filter(
      (contentEntry) =>
        contentEntry.bundleType === 'chunk' && contentEntry.type === 'js' && !contentEntry.isStore
    );
    await Promise.all(
      chunks.map(async (contentEntry) => {
        await remoteDynamicImportModule(
          joinPath(contentURLPrefix, contentEntry.path),
          appName,
          appManifest.appType,
          'chunk',
          false
        );
      })
    );

    // 2. Load the app bundle
    const otherBundles = appManifest.content.filter(
      (contentEntry) =>
        contentEntry.isMainBundle === false &&
        contentEntry.bundleType === 'bundle' &&
        contentEntry.type === 'js'
    );
    // 2.1 Load non-main bundles first
    await Promise.all(
      otherBundles.map((contentEntry) => {
        remoteDynamicImportModule(
          joinPath(contentURLPrefix, contentEntry.path),
          appName,
          appManifest.appType,
          'bundle',
          false
        );
      })
    );

    // 2.2 Then load main bundle
    const mainBundle = appManifest.content.find(
      (contentEntry) =>
        contentEntry.isMainBundle === true &&
        contentEntry.bundleType === 'bundle' &&
        contentEntry.type === 'js'
    );

    if (appManifest.jsModuleFormat === 'umd') {
      return remoteDynamicImportMainUMDModule(
        joinPath(contentURLPrefix, mainBundle.path),
        appName,
        appManifest.appType
      );
    } else if (appManifest.appType === 'ember') {
      await remoteDynamicImportModule(
        joinPath(contentURLPrefix, mainBundle.path),
        appName,
        'ember',
        'bundle',
        true
      );

      return Promise.resolve(window.require('application/app'));
    } else {
      return remoteDynamicImportModule(
        joinPath(contentURLPrefix, mainBundle.path),
        appName,
        appManifest.appType,
        'bundle',
        true
      );
    }
  } catch (error) {
    console.error(`Failed to load with error ${error}`);
  }
}

export const loadLocalStore = (name, storeInstance, globalEventDistributor) => {
  if (!storeInstance || !globalEventDistributor)
    console.log(`Could not load storeInstance of app ${name}.`);

  // register the store with the globalEventDistributor
  globalEventDistributor.registerStore(storeInstance);
};

export const loadESModuleStoreAsCustomProps = async (
  name,
  storeBundleURL,
  storeChunkURL,
  globalEventDistributor,
  initialReduxStoreState
) => {
  let storeModule = {},
    customProps = { globalEventDistributor };

  // try to import the store module
  try {
    if (storeChunkURL && storeChunkURL !== '')
      await import(/* webpackIgnore: true */ storeChunkURL);
    storeModule = storeBundleURL
      ? await import(/* webpackIgnore: true */ storeBundleURL)
      : { storeInstance: null };
  } catch (e) {
    console.log(`Could not load store of app ${name}.`, e);
  }

  if (storeModule.storeInstance && globalEventDistributor) {
    // add a reference of the store to the customProps
    customProps.store = storeModule.storeInstance;

    // register the store with the globalEventDistributor
    globalEventDistributor.registerStore(storeModule.storeInstance);

    // initialize microfrontend store with current shell state
    storeModule.storeInstance.dispatch({
      type: 'STORE_INIT',
      shellInitState: initialReduxStoreState(),
    });

    console.log(`loadStoreAsCustomProps ES Module registerStore successfully for app ${name}`);
  }

  console.log(
    `loadStoreAsCustomProps ES Module successfully for app ${name} customProps ${JSON.stringify(
      customProps
    )}`
  );

  return customProps;
};

export const loadUMDModuleStoreAsCustomProps = async (
  name,
  storeBundleURL,
  storeChunkURL,
  globalEventDistributor,
  initialReduxStoreState
) => {
  let storeModule = {},
    customProps = { globalEventDistributor };

  // try to import the store module
  try {
    if (storeChunkURL && storeChunkURL !== '')
      await import(/* webpackIgnore: true */ storeChunkURL);
    if (storeBundleURL) {
      await import(/* webpackIgnore: true */ storeBundleURL);
      storeModule = getScriptModule(name);
    } else storeModule = { storeInstance: null };
  } catch (e) {
    console.log(`Could not load store of app ${name}.`, e);
  }

  if (storeModule.storeInstance && globalEventDistributor) {
    // add a reference of the store to the customProps
    customProps.store = storeModule.storeInstance;

    // register the store with the globalEventDistributor
    globalEventDistributor.registerStore(storeModule.storeInstance);

    // initialize microfrontend store with current shell state
    storeModule.storeInstance.dispatch({
      type: 'STORE_INIT',
      shellInitState: initialReduxStoreState(),
    });

    console.log(`loadStoreAsCustomProps UMD registerStore successfully for app ${name}`);
  }

  console.log(
    `loadStoreAsCustomProps UMD successfully for app ${name} customProps ${JSON.stringify(
      customProps
    )}`
  );

  return customProps;
};
